import { Stack, Typography } from '@mui/material';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { formatDate, UserAvatar } from '@side/pantry';
import { formatAddress } from '@/common/utils/address';
import { formatDisplayName } from '@/common/utils/formatDisplayName';
import type {
  CompensationReportFiltersFormValues,
  ReportingTransactionsSchema,
} from '../CompensationReportFilters/CompensationReportFilters.helpers';
import {
  buildLeadSourceFilter,
  formatAmount,
  formatPercentage,
  getReportingPeriodDateRange,
} from '../helpers';
import type { ReportsDataGroup } from '../ReportsConfiguration/useReportsRole';
import type { TeamDisplayMap, UserDisplayMap } from '../types';

export type ReportingTransactionResult =
  ReportingTransactionsSchema['responses']['200']['content']['application/json']['results'][0];

const TRANSACTION_TYPE_LABEL: Record<
  ReportingTransactionResult['transactionType'],
  string
> = {
  LISTING: 'Listing',
  OFFER: 'Offer',
  REFERRAL: 'Referral',
  LEASE_LANDLORD: 'Lease (Landlord)',
  LEASE_TENANT: 'Lease (Tenant)',
};

/** Row type: API result plus id and the three display-mapped fields (address, agent, team). */
export type DealAllocationsTableRow = ReportingTransactionResult & {
  id: string;
  agent: string;
  agentAvatar: string | null | undefined;
  team: string;
};

export function mapReportingTransactionToRow(
  r: ReportingTransactionResult,
  displayMaps?: {
    usersById: UserDisplayMap;
    teamsById: TeamDisplayMap;
  },
): DealAllocationsTableRow {
  const agentId = r.teamMemberIdentityId || r.teamMemberId;
  const agent = displayMaps?.usersById[r.teamMemberIdentityId ?? ''];
  const agentName = agent
    ? formatDisplayName({
        firstName: agent.firstName,
        lastName: agent.lastName,
      })
    : agentId;
  const team =
    displayMaps?.teamsById[r.teamIdentityId]?.alias ??
    displayMaps?.teamsById[r.teamIdentityId]?.legalName ??
    r.teamIdentityId;
  return {
    ...r,
    id: `${r.teamMemberIdentityId}-${r.commissionId}`,
    agent: agentName,
    agentAvatar: agent?.photoUrl,
    team,
  };
}

export enum DealAllocationsTableColumns {
  closeDate = 'closeDate',
  transactionType = 'transactionType',
  addressStreet = 'addressStreet',
  addressCity = 'addressCity',
  addressZip = 'addressZip',
  listPrice = 'listPrice',
  salePrice = 'salePrice',
  weightedSalePrice = 'weightedSalePrice',
  agent = 'agent',
  team = 'team',
  leadSourceName = 'leadSourceName',
  leadSourceDetail = 'leadSourceDetail',
  commissionPercentage = 'commissionPercentage',
  startingGrossCommission = 'startingGrossCommission',
  preFees = 'preFees',
  adjustedGrossCommission = 'adjustedGrossCommission',
  agentAllocationPercentage = 'agentAllocationPercentage',
  weightedAdjustedGrossCommission = 'weightedAdjustedGrossCommission',
  agentSharePercentage = 'agentSharePercentage',
  agentGrossShare = 'agentGrossShare',
  agentFeesReceived = 'agentFeesReceived',
  agentFeesPaid = 'agentFeesPaid',
  agentNetFees = 'agentNetFees',
  agentNetShare = 'agentNetShare',
  teamSharePercentage = 'teamSharePercentage',
  teamGrossShare = 'teamGrossShare',
  teamFeesReceived = 'teamFeesReceived',
  teamFeesPaid = 'teamFeesPaid',
  teamNetFees = 'teamNetFees',
  teamNetShare = 'teamNetShare',
  brandSharePercentage = 'brandSharePercentage',
  brandGrossShare = 'brandGrossShare',
  brandFeesReceived = 'brandFeesReceived',
  brandFeesPaid = 'brandFeesPaid',
  brandNetFees = 'brandNetFees',
  brandNetShare = 'brandNetShare',
  sideSharePercentage = 'sideSharePercentage',
  sideGrossShare = 'sideGrossShare',
  sideFeesReceived = 'sideFeesReceived',
  sideFeesPaid = 'sideFeesPaid',
  sideNetFees = 'sideNetFees',
  sideNetShare = 'sideNetShare',
}

export const columns: GridColDef<DealAllocationsTableRow>[] = [
  {
    field: DealAllocationsTableColumns.closeDate,
    headerName: 'Close',
    valueFormatter: (value) => formatDate(value ?? '', 'shortDate'),
    flex: 0.5,
    minWidth: 100,
    hideable: false,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.transactionType,
    headerName: 'Type',
    valueFormatter: (value: DealAllocationsTableRow['transactionType']) =>
      value != null ? (TRANSACTION_TYPE_LABEL[value] ?? value) : '',
    minWidth: 100,
    flex: 0.5,
    hideable: false,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.addressStreet,
    headerName: 'Address',
    minWidth: 220,
    flex: 2,
    hideable: false,
    renderCell: (params) =>
      formatAddress({
        street1: params.row.addressStreet,
        city: params.row.addressCity,
        zip: params.row.addressZip,
      }),
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.addressCity,
    headerName: 'City',
    minWidth: 120,
    flex: 1,
    valueFormatter: (v) => v ?? '',
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.addressZip,
    headerName: 'Zip',
    minWidth: 120,
    flex: 1,
    valueFormatter: (v) => v ?? '',
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.listPrice,
    headerName: 'List Price',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    hideable: true,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.salePrice,
    headerName: 'Deal Value',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    hideable: false,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.weightedSalePrice,
    headerName: 'Weighted Deal Value',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.agent,
    headerName: 'Agent',
    sortable: true,
    renderCell: (params) => (
      <Stack
        direction="row"
        sx={{ gap: 1, alignItems: 'center', height: '100%' }}
      >
        <UserAvatar
          name={params.row.agent}
          avatarUrl={params.row.agentAvatar ?? undefined}
          sx={{ width: 30, height: 30 }}
        />
        <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
          {params.value}
        </Typography>
      </Stack>
    ),
    minWidth: 178,
    flex: 1,
    hideable: false,
  },
  {
    field: DealAllocationsTableColumns.team,
    headerName: 'Team',
    minWidth: 138,
    flex: 1,
    hideable: false,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.leadSourceName,
    headerName: 'Lead Source',
    minWidth: 120,
    flex: 1,
    valueFormatter: (v) => v ?? '',
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.leadSourceDetail,
    headerName: 'Lead Source Detail',
    minWidth: 120,
    flex: 1,
    valueFormatter: (v) => v ?? '',
    sortable: false,
  },
  {
    field: DealAllocationsTableColumns.commissionPercentage,
    headerName: 'Commission %',
    valueFormatter: formatPercentage,
    minWidth: 100,
    flex: 0.5,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.startingGrossCommission,
    headerName: 'GCI',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.preFees,
    headerName: 'Pre-Split Deductions',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.adjustedGrossCommission,
    headerName: 'Post-Referral GCI',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    hideable: false,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.agentAllocationPercentage,
    headerName: 'Allocation',
    valueFormatter: formatPercentage,
    minWidth: 100,
    flex: 0.5,
    hideable: false,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.weightedAdjustedGrossCommission,
    headerName: 'Weighted GCI',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    hideable: false,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.agentSharePercentage,
    headerName: 'Agent Split %',
    valueFormatter: formatPercentage,
    minWidth: 100,
    flex: 0.5,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.agentGrossShare,
    headerName: 'Agent Gross Commission',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.agentFeesReceived,
    headerName: 'Agent Fees Received',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.agentFeesPaid,
    headerName: 'Agent Fees Paid',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.agentNetFees,
    headerName: 'Agent Net Fees',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.agentNetShare,
    headerName: 'Agent Net Commission',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.teamSharePercentage,
    headerName: 'Team Split %',
    valueFormatter: formatPercentage,
    minWidth: 100,
    flex: 0.5,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.teamGrossShare,
    headerName: 'Team Gross Commission',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.teamFeesReceived,
    headerName: 'Team Fees Received',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.teamFeesPaid,
    headerName: 'Team Fees Paid',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.teamNetFees,
    headerName: 'Team Net Fees',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.teamNetShare,
    headerName: 'Team Net Commission',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.brandSharePercentage,
    headerName: 'Brand Split %',
    valueFormatter: formatPercentage,
    minWidth: 100,
    flex: 0.5,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.brandGrossShare,
    headerName: 'Brand Gross Commission',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.brandFeesReceived,
    headerName: 'Brand Fees Received',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.brandFeesPaid,
    headerName: 'Brand Fees Paid',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.brandNetFees,
    headerName: 'Brand Net Fees',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.brandNetShare,
    headerName: 'Brand Net Commission',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.sideSharePercentage,
    headerName: 'Side Split %',
    valueFormatter: formatPercentage,
    minWidth: 100,
    flex: 0.5,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.sideGrossShare,
    headerName: 'Side Gross Commission',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.sideFeesReceived,
    headerName: 'Side Fees Received',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.sideFeesPaid,
    headerName: 'Side Fees Paid',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.sideNetFees,
    headerName: 'Side Net Fees',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: DealAllocationsTableColumns.sideNetShare,
    headerName: 'Side Net Commission',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
];

export const VISIBILITY_MODEL: Record<DealAllocationsTableColumns, boolean> = {
  addressStreet: true,
  adjustedGrossCommission: true,
  agent: true,
  weightedAdjustedGrossCommission: true,
  agentAllocationPercentage: true,
  closeDate: true,
  salePrice: true,
  weightedSalePrice: false,
  team: true,
  transactionType: true,
  listPrice: false,
  addressCity: false,
  addressZip: false,
  leadSourceName: false,
  leadSourceDetail: false,
  commissionPercentage: false,
  startingGrossCommission: false,
  preFees: false,
  agentSharePercentage: false,
  agentGrossShare: false,
  agentFeesReceived: false,
  agentFeesPaid: false,
  agentNetFees: false,
  agentNetShare: false,
  teamSharePercentage: false,
  teamGrossShare: false,
  teamFeesReceived: false,
  teamFeesPaid: false,
  teamNetFees: false,
  teamNetShare: false,
  brandSharePercentage: false,
  brandGrossShare: false,
  brandFeesReceived: false,
  brandFeesPaid: false,
  brandNetFees: false,
  brandNetShare: false,
  sideSharePercentage: false,
  sideFeesReceived: false,
  sideFeesPaid: false,
  sideNetFees: false,
  sideNetShare: false,
  sideGrossShare: false,
};

export const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

export const DEFAULT_PAGE_SIZE = 25;

export function buildReportingTransactionsQuery(
  filters: CompensationReportFiltersFormValues | undefined,
  sortModel: GridSortModel,
  viewReportAs: string,
  { offset, limit }: { offset: number; limit: number },
  allTimeStartDate?: string,
): ReportingTransactionsSchema['parameters']['query'] {
  const { closingDateStart, closingDateEnd } = getReportingPeriodDateRange(
    filters?.reportingPeriod ?? new Date().getFullYear(),
    {
      start: filters?.customDateStart,
      end: filters?.customDateEnd,
    },
    allTimeStartDate,
  );
  const sort = sortModel[0];
  const sortBy = sort?.field ?? 'closeDate';
  const sortDirection: 'ASC' | 'DESC' = sort?.sort === 'asc' ? 'ASC' : 'DESC';

  const leadSourceFilter = buildLeadSourceFilter(filters);

  return {
    viewReportAs,
    offset,
    limit,
    // we need to cast to NonNullable<ReportingTransactionsSchema['parameters']['query']>['sortBy'] because GridSortModel doesn't have a type for the sortBy field
    sortBy: sortBy as NonNullable<
      ReportingTransactionsSchema['parameters']['query']
    >['sortBy'],
    sortDirection,
    closingDateStart,
    closingDateEnd,
    transactionStatus: ['CLOSED'],
    transactionType: filters?.transactionType,
    excludeTerminatedAgents: filters?.hideTerminatedAgents ?? false,
    agentIdentityIds: filters?.agents?.length
      ? filters.agents.map((a) => a.value)
      : undefined,
    teamIdentityIds: filters?.teams?.length
      ? filters.teams.map((t) => t.value)
      : undefined,
    ...leadSourceFilter,
  };
}

export function compareByCloseDateThenAddress(
  a: ReportingTransactionResult,
  b: ReportingTransactionResult,
): number {
  const dateCompare = (b.closeDate ?? '').localeCompare(a.closeDate ?? '');
  if (dateCompare !== 0) return dateCompare;
  return (a.addressStreet ?? '').localeCompare(b.addressStreet ?? '');
}

export const dealAllocationsDataGroups: Record<
  DealAllocationsTableColumns,
  ReportsDataGroup
> = {
  closeDate: 'agent',
  transactionType: 'agent',
  addressStreet: 'agent',
  addressCity: 'agent',
  addressZip: 'agent',
  listPrice: 'agent',
  salePrice: 'agent',
  agent: 'agent',
  team: 'agent',
  leadSourceName: 'agent',
  leadSourceDetail: 'agent',
  commissionPercentage: 'agent',
  startingGrossCommission: 'agent',
  preFees: 'agent',
  adjustedGrossCommission: 'agent',
  agentAllocationPercentage: 'agent',
  weightedAdjustedGrossCommission: 'agent',
  weightedSalePrice: 'agent',
  agentSharePercentage: 'agent',
  agentGrossShare: 'agent',
  agentFeesReceived: 'agent',
  agentFeesPaid: 'agent',
  agentNetFees: 'agent',
  agentNetShare: 'agent',
  teamSharePercentage: 'team',
  teamGrossShare: 'team',
  teamFeesReceived: 'team',
  teamFeesPaid: 'team',
  teamNetFees: 'team',
  teamNetShare: 'team',
  brandSharePercentage: 'brand',
  brandGrossShare: 'brand',
  brandFeesReceived: 'brand',
  brandFeesPaid: 'brand',
  brandNetFees: 'brand',
  brandNetShare: 'brand',
  sideSharePercentage: 'side',
  sideGrossShare: 'side',
  sideFeesReceived: 'side',
  sideFeesPaid: 'side',
  sideNetFees: 'side',
  sideNetShare: 'side',
};
